
import { CONFIG } from '../../config';
import { IEmailFields } from '../../models/EmailTypes';
import { IChecklistInfoResponse } from '../../components/checklists/Checklist';
import { UserFacingError } from '../../errors/UserFacingError';
import { IChecklistResponse } from '../../components/checklists/utils/submitChecklist';
import { IIncidentReportResponse } from '../../components/health_safety/utils/submitIncidentReport';
import {IAttachment, Api as ApiOnePlaceComponents, IApiClientConfig} from 'oneplace-components'
import {IIncident, IIncidentReportSurveyResponse, IIncidentSignResponse, IIncidentV2} from '../../models/Incident';
import { IDocumentShareAcknowledgement, IDocumentShareView } from '../../models/DocumentShare';


export type ScheduleType = 'completed' | 'week' | 'overdue' | 'today';

export interface IApi {   
    getAuthenticatedPhotoUrl(url: string): string
    submitChecklist(checklistId: number, checklistData: FormData, timeout: number): Promise<IChecklistResponse>;
    sendChecklistEmail(checklistId: number, message: IEmailFields): Promise<void>;
    fetchChecklist(): Promise<IChecklistInfoResponse>;
    saveAttachment(attachment: IAttachment): Promise<any>
    viewAttachment(attachment: IAttachment): void
    deleteAttachment(attachment: IAttachment): Promise<any>

    fetchIncidentSign(): Promise<IIncidentSignResponse>;
    submitIncidentSign(incident: IIncident | IIncidentV2): Promise<any>;

    // anonymous creation of a new incident, mirroring fetchChecklist/submitChecklist above
    fetchIncidentReportSurvey(): Promise<IIncidentReportSurveyResponse>;
    submitIncidentReport(incidentReportData: FormData, timeout: number): Promise<IIncidentReportResponse>;
}

export class Api implements IApi {

    region: string;
    path: string;
    constructor(region :string, path: string){
        this.region = region;
        this.path = path;
    }

    getAuthenticatedPhotoUrl(url: string): string {        
        return new ApiOnePlaceComponents().getAuthenticatedPhotoUrl(url)
    }

    async submitChecklist(checklistId: number, checklistData: FormData, timeout: number) {
        try {
            const res = await fetch(
                `${this.getBaseUrl()}`,
                {
                    method: 'POST',
                    headers: {
                        'X-Survey-Version': CONFIG.apiVersion
                    },
                    redirect: 'follow',
                    body: checklistData
                }
            );
            if (!res.ok) throw res;
            const response = await res.json()
            if (!response.success && typeof response.data == 'string') {
                throw new UserFacingError(response.data);
            }
            if (!response.data || !response.data.Location) {
                throw new Error('Error submitting Survey.');
            }
            
            const checklistResponse = {
                checklistId : response.data.Location.split('/').slice(-2)[0],
                score: response.data.score,
                unansweredQuestions: response.data.unansweredQuestions
            };
            return checklistResponse as IChecklistResponse;
        } catch (error) {
            console.error('Error:', error);
            try {
                const errResponse = await error.json()
              
                throw new Error('Error submitting Survey. ' + errResponse.data);
            } catch (e) {
                if (e instanceof Error) {
                    throw e
                } else{
                    throw new Error('Error submitting Survey. ' + e);
                }
            }
        }
    }

    async sendChecklistEmail(checklistId: number, message: IEmailFields) {
        try {
            const res = await fetch(
                `${this.getBaseUrl()}/${checklistId}/email`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-Survey-Version': CONFIG.apiVersion
                        },
                        redirect: 'follow',
                        body: JSON.stringify({ message })
                    }
            );
            const response = await res.json()
            if (!response.success) {
                throw new UserFacingError(!response.data ? 'Email sending failed. Response: ' + JSON.stringify(response) : response.data);
            }
            return response;
        } catch (e) {
            console.error('Error:', e);
            throw new UserFacingError('Email sendingnpm star failed. Response: ' + JSON.stringify(e));
        }
        
    }

    async fetchChecklist(){
        try {
            const res = await fetch(
                `${this.getBaseUrl()}`, 
                 {  
                     headers: {'X-Survey-Version': CONFIG.apiVersion},
                     redirect: 'follow'
                }
            );         
            if (res.url) {
                // old US/CA clients have old url pointing to the sydney/pacific region.  
                /// We detect if a redirect happens(old US/CA clients) and save the new region to use when we post the checklist
                if(res.url !== this.getBaseUrl()) {
                    const path = res.url.split("survey/")
                    if (path.length  >= 2) {
                        this.region =  path[1].substring(0,2)
                        console.log (this.region)
                    }
                }
            }
            const data = await res.json();            
            return data;
        } catch (err) {
            return { 
                success: false,
                data: "network error",
              
            };
        }
    }  

    // The incident survey posts to a different resource than the checklist one (nested under
    // /incident-report), so the pre-upload endpoint is picked per `attachment.forEntity`, which
    // Question.tsx sets before either method below is called.
    getAttachmentUrl(attachment: IAttachment){
        return attachment.forEntity === 'incidentv2'
            ? `${this.getBaseUrl()}/incident-report/incidentAttachment`
            : `${this.getBaseUrl()}/checklistAttachment`
    }

    async saveAttachment(attachment: IAttachment) {
        const config :IApiClientConfig = {
            url: this.getAttachmentUrl(attachment),
            header: {'X-Survey-Version': CONFIG.apiVersion},
            timeout: 300000
        } 
        return new ApiOnePlaceComponents(config).saveAttachment(attachment)
    }

    viewAttachment(attachment: IAttachment){
        if(!attachment.url) return;
        window.open(attachment.url)
    }

    async deleteAttachment(attachment: IAttachment){
        const config :IApiClientConfig = {
            url: this.getAttachmentUrl(attachment),
            header: {'X-Survey-Version': CONFIG.apiVersion},
            timeout: 60000
        } 
        return new ApiOnePlaceComponents(config).deleteAttachment(attachment)
    }

    async fetchIncidentSign(){
        try {
            const res = await fetch(
                `${CONFIG.apiUrl}/${this.region}/${this.path}/incident-sign`, 
                 {  
                     redirect: 'follow'
                }
            );  
            if(!res.ok) throw res;
            return await res.json();
        } catch (res) {
            const responseType = res.headers.get('content-type');
            let errResponse;
            if(responseType && 
                responseType.indexOf('application/json') !== -1) {                
                errResponse = await res.json()   
                throw new Error(errResponse.data)
            } else if(responseType && 
                responseType.indexOf('text/plain') !== -1) {
                errResponse = await res.text()   
                throw new Error(errResponse)
            } else {
                throw new Error(res)
            }
        }
    }  

    async submitIncidentSign(incident: IIncident | IIncidentV2) {
        try {                        
            const res = await fetch(
                `${CONFIG.apiUrl}/${this.region}/${this.path}/incident-sign`, 
                {
                    method: 'PUT',
                    redirect: 'follow',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify(incident)
                }
            );          
            if (!res.ok) throw res;
        } catch (response) {
            console.error('Error:', response);
            const responseType = response.headers.get("content-type");
            if(responseType.indexOf("application/json") !== -1){
                const errResponse = await response.json()   
                throw new Error(errResponse.data); 
            } else {
                const errResponse = await response.text()   
                throw new Error(errResponse); 
            }
        }
    }

    // anonymous incident survey

    async fetchIncidentReportSurvey() {
        try {
            const res = await fetch(
                `${this.getBaseUrl()}/incident-report`,
                {
                    headers: {'X-Survey-Version': CONFIG.apiVersion},
                    redirect: 'follow'
                }
            );
            const data = await res.json();
            return data;
        } catch (err) {
            return {
                success: false,
                data: "network error"
            };
        }
    }

    async submitIncidentReport(incidentReportData: FormData, timeout: number = 300000) {
        try {
            const res = await fetch(
                `${this.getBaseUrl()}/incident-report`,
                {
                    method: 'POST',
                    headers: {
                        'X-Survey-Version': CONFIG.apiVersion
                    },
                    redirect: 'follow',
                    body: incidentReportData
                }
            );
            if (!res.ok) throw res;
            const response = await res.json();
            if (!response.success && typeof response.data == 'string') {
                throw new UserFacingError(response.data);
            }
            return {
                incidentNo: response.data?.incidentNo
            } as IIncidentReportResponse;
        } catch (error) {
            console.error('Error:', error);
            try {
                const errResponse = await error.json();
                throw new Error('Error submitting Incident. ' + errResponse.data);
            } catch (e) {
                if (e instanceof Error) {
                    throw e;
                } else {
                    throw new Error('Error submitting Incident. ' + e);
                }
            }
        }
    }

    async getMagicDownloadLink(token: string, email: string): Promise<any> {
    try {
        const response = await fetch(
            `${CONFIG.apiUrl}/${this.region}/email-attachments/${token}`,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email })
            }
        );

        //Errors that not reject the promise e.g.: Expired link code 410 Gone
        if (!response.ok) throw response;

        const data = await response.json();
        return data.message;
    } catch (response) {
        if (response instanceof Error) {
            throw response;
        }

        // Handle error response
        const errorText = (await response.json()).message;
        if(errorText)
            throw new Error(errorText);

        throw new Error('Failed to get download link');
    }
}

    /**
     * Step 2 of the two-step email-gate flow for external compliance document recipients.
     *
     * Submits the recipient's email address to the server; if it matches the address on file
     * for the share, the server sends a magic-link email and resolves the promise.
     *
     * Throws an Error (with `message` set to the server's response message) on 404 (link not
     * found / expired) or 400 (email mismatch / validation error).
     *
     * @param requestToken  The plaintext UUID from the first-link URL segment
     * @param email         The email address entered by the recipient
     */
    async requestDocumentShareLink(requestToken: string, email: string): Promise<void> {
        const response = await fetch(
            `${CONFIG.apiUrl}/${this.region}/document-share/${requestToken}/request`,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email }),
            }
        );

        if (!response.ok) {
            let message = 'Something went wrong. Please try again.';
            try {
                const body = await response.json();
                if (body && body.message) message = body.message;
            } catch (_) {
                // ignore parse errors — keep the default message
            }
            throw new Error(message);
        }
    }

    async getDocumentShare(token: string): Promise<IDocumentShareView> {
        const response = await fetch(
            `${CONFIG.apiUrl}/${this.region}/document-share/${token}`,
            { redirect: 'follow' }
        );

        if (!response.ok) {
            if (response.status === 404) {
                throw new Error('This link is invalid or has expired.');
            }
            let message = 'Unable to load the document. Please try again.';
            try {
                const body = await response.json();
                if (body && body.message) message = body.message;
            } catch (_) {
                // ignore parse errors — keep the default message
            }
            throw new Error(message);
        }

        return await response.json();
    }

    async acknowledgeDocumentShare(
        token: string,
        acknowledgement: IDocumentShareAcknowledgement
    ): Promise<void> {
        const response = await fetch(
            `${CONFIG.apiUrl}/${this.region}/document-share/${token}/acknowledge`,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(acknowledgement),
            }
        );

        if (!response.ok) {
            if (response.status === 409) {
                throw new Error('This document has already been acknowledged.');
            }
            let message = 'Unable to record your acknowledgement. Please try again.';
            try {
                const body = await response.json();
                if (body && body.message) message = body.message;
            } catch (_) {
                // ignore parse errors — keep the default message
            }
            throw new Error(message);
        }
    }

    documentShareAttachmentUrl(token: string, attachmentId: number): string {
        return `${CONFIG.apiUrl}/${this.region}/document-share/${token}/attachments/${attachmentId}`;
    }

    /**
     * Fetches the document view for a fully public magic link (no email gate, no login).
     *
     * The server always returns `signOff: "HIDDEN"` for public links — the frontend should
     * not render an acknowledgement form.
     *
     * Throws an Error with a user-readable `message` on 404 (link revoked / not found) or
     * any other non-2xx response.
     *
     * @param token The plaintext UUID from the public link URL segment
     */
    async getPublicDocumentShare(token: string): Promise<IDocumentShareView> {
        const response = await fetch(
            `${CONFIG.apiUrl}/${this.region}/document-public/${token}`,
            { redirect: 'follow' }
        );

        if (!response.ok) {
            if (response.status === 404) {
                throw new Error('This link is invalid or has been revoked.');
            }
            let message = 'Unable to load the document. Please try again.';
            try {
                const body = await response.json();
                if (body && body.message) message = body.message;
            } catch (_) {
                // ignore parse errors — keep the default message
            }
            throw new Error(message);
        }

        return await response.json();
    }

    /** Returns the URL for downloading an attachment via a fully public magic link. */
    publicDocumentShareAttachmentUrl(token: string, attachmentId: number): string {
        return `${CONFIG.apiUrl}/${this.region}/document-public/${token}/attachments/${attachmentId}`;
    }

    getBaseUrl(){
        return  `${CONFIG.apiUrl}/${this.region}/${this.path}`
    }
}
